import _ from 'lodash';
import { FormApi, ValidationErrors } from 'final-form';
import * as React from 'react';
import classnames from 'classnames';
import { Field, Form, FormRenderProps } from 'react-final-form';
import FontAwesomeIcon from 'react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { OnChange } from 'react-final-form-listeners';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { ProductCategory, Store } from './types';
import { saveUpdateProductCategory } from './actions/Actions';
import { CATEGORIES_NS, CLOUDINARY_BASE_URL } from './Constants';

type FormValues = Partial<ProductCategory>;

const getImageNameWithoutExtension = (name: string): string =>
    name.split('.').slice(0, -1).join('.');

const Categories: React.FC<RouteComponentProps> = () => {
    const { t } = useTranslation();
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();
    const [loading, setLoading] = React.useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = React.useState<number>(null);
    const [selectedImageId, setSelectedImageId] = React.useState<number>(null);
    const [formValues, setFormValues] = React.useState<FormValues>(null);

    const productCategories = useSelector(({ app }: Store) => app.productCategories);
    const categoryImages = useSelector(({ app }: Store) => app.categoryImages);

    const getImageName = React.useCallback(
        (imageId: number): string =>
            (categoryImages.find(({ id }) => id === imageId) || {}).name,
        [categoryImages]
    );

    const setSelectedImage = React.useCallback(
        (id: number) => () => {
            setSelectedImageId(id);
            setFormValues({ ...formValues, imageID: id });
        },
        [formValues]
    );

    const setSelectedCategory = React.useCallback(
        (categoryId: number) => () => {
            const selectedCategory = productCategories.find(
                ({ id }) => id === categoryId
            );
            setSelectedCategoryId(categoryId);
            setSelectedImageId(selectedCategory.imageID);
            setFormValues(selectedCategory);
        },
        [productCategories]
    );

    const resetStateAndForm = React.useCallback(() => {
        setSelectedCategoryId(null);
        setSelectedImageId(null);
        setFormValues({});
    }, []);

    const validateInput = React.useCallback(
        (values: FormValues): ValidationErrors => {
            const errors: ValidationErrors = {};

            if (_.isEmpty(values.name)) {
                errors.name = t('required_field');
            }

            if (_.isNil(values.imageID)) {
                errors.imageID = t('required_field');
            }

            return errors;
        },
        [t]
    );

    const handleFormSubmit = React.useCallback(
        (values: FormValues, form: FormApi) => {
            setLoading(true);

            dispatch(saveUpdateProductCategory(values))
                .then(() => {
                    resetStateAndForm();
                    form.reset({});

                    ['name', 'imageID'].forEach(key => {
                        form.resetFieldState(key);
                    });
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [dispatch, resetStateAndForm]
    );

    const renderForm = React.useCallback(
        ({ handleSubmit, submitting, validating }: FormRenderProps<FormValues>) => {
            const disabled = submitting || validating;

            return (
                <form className='form' onSubmit={handleSubmit}>
                    <label className='form-field'>
                        <label>{t(`${CATEGORIES_NS}.name`)}:</label>
                        <Field name='name'>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} type='text' />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                        <OnChange name='name'>
                            {value => {
                                setFormValues({ ...formValues, name: value });
                            }}
                        </OnChange>
                    </label>
                    <label className='form-field'>
                        <label>{t(`${CATEGORIES_NS}.icon`)}:</label>
                        <Field name='imageID'>
                            {({ input, meta }) => (
                                <div className='field-input imageId'>
                                    {!_.isNil(input.value) &&
                                        _.isNumber(input.value) && (
                                            <img
                                                {...input}
                                                src={`${CLOUDINARY_BASE_URL}/productCategories/${getImageNameWithoutExtension(
                                                    getImageName(input.value)
                                                )}_bg.png`}
                                            />
                                        )}
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <div className='action-buttons'>
                        <div>
                            <button className='save' type='submit' disabled={disabled}>
                                {t('save')}
                            </button>
                            {loading && (
                                <FontAwesomeIcon name='spinner' className='fa-spin' />
                            )}
                        </div>
                        {/* <button className='delete' type='button' disabled={disabled}>
                        ΔΙΑΓΡΑΦΗ
                    </button> */}
                    </div>
                </form>
            );
        },
        [formValues, getImageName, loading, t]
    );

    const groupedImages = _.groupBy(categoryImages, i => i.type);

    return (
        <div className='admin-form main-content'>
            <Sidebar>
                <FontAwesomeIcon
                    name='plus'
                    className='add-new'
                    onClick={resetStateAndForm}
                />
                <div className='content'>
                    {productCategories.map(({ id, name }) => (
                        <div
                            className={classnames('item', {
                                selected: id === selectedCategoryId
                            })}
                            key={id}
                            onClick={setSelectedCategory(id)}>
                            {name}
                        </div>
                    ))}
                </div>
            </Sidebar>
            <div className='category-form'>
                <Form
                    onSubmit={handleFormSubmit}
                    render={renderForm}
                    validate={validateInput}
                    initialValues={{ ...formValues }}
                />
                <div className='category-images'>
                    {Object.keys(groupedImages).map(type => (
                        <div className='group' key={type}>
                            <div className='type'>{t(type)}</div>
                            <div className='images'>
                                {groupedImages[type].map(({ id, name }) => (
                                    <img
                                        src={`${CLOUDINARY_BASE_URL}/productCategories/${getImageNameWithoutExtension(
                                            name
                                        )}_sml.png`}
                                        key={id}
                                        className={classnames({
                                            selected: id === selectedImageId
                                        })}
                                        onClick={setSelectedImage(id)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Categories;
